import {useCallback, useEffect, useMemo} from 'react';
import {useFilters} from './useFilters';
import {omit} from 'lodash';
import {trackEvent} from '@lib/infrastructure';
import {AggregateFilterConfig} from '../util';
import {
  FilterConfigSections,
  FilterOption,
  FilterSelectionSections,
  FilterSliceKeyType,
  nonEmpty,
  useDebouncedEffect,
  useMemoArray,
} from '@lib/utils';

type FilterDataReturnType<TEntity> = {
  config: FilterConfigSections;
  filteredData: TEntity[];
  resetFilters: () => void;
  removeFilter: (sectionId: string, filterId: string) => boolean;
  selectedFilters: FilterSelectionSections;
};

export const useFilterData = <TEntity, TFilterOption extends FilterOption>(
  sliceKey: FilterSliceKeyType,
  cacheKeyInput: string,
  optionsToFilter: TEntity[],
  filtersInput: AggregateFilterConfig,
  sectionCombinationStrategy: 'and' | 'or' = 'and',
  isLoadingData = false,
): FilterDataReturnType<TEntity> => {
  const {
    selectedFilters,
    resetFilters,
    removeFilter,
    config,
    setConfig,
    setCacheKey,
    cacheKey,
    getInitFilterSelection,
  } = useFilters(sliceKey);

  const configWithoutPredicates = useMemo(
    () =>
      // TODO: fix types, there are `any`s everywhere here
      // we remove predicates as they are not relevant for config, and storing and mathing them is problematic
      Object.entries(filtersInput).reduce(
        // Note: omit(undefined, ['']) returns {}, which is not valid. The type system should have caught this,
        // but that's a bigger problem to solve. For now, only calling `omit` on non-undefined values fixes things
        (acc, [sectionId, section]) => ({...acc, [sectionId]: section && omit(section, ['predicate'])}),
        {},
      ),
    [filtersInput],
  );

  useEffect(() => {
    setConfig(configWithoutPredicates);
  }, [configWithoutPredicates, setConfig]);

  useEffect(() => {
    if (cacheKeyInput === cacheKey) return;

    // to be sure that selection will be properly reset, we send new selection that we are sure
    // its based on latest configuration
    setCacheKey(cacheKeyInput, getInitFilterSelection(configWithoutPredicates));
  }, [cacheKey, cacheKeyInput, configWithoutPredicates, getInitFilterSelection, setCacheKey]);

  const matchers = useMemo(() => {
    const allSectionsSelectedIds = nonEmpty(Object.values(selectedFilters).flatMap((x) => x));

    return nonEmpty(Object.values(filtersInput)).map(({type, predicate, sectionId, data}) => (entity: TEntity) => {
      const selectedValuesForSection = nonEmpty(
        (selectedFilters[sectionId] ?? []).map((filterId) => data.find((d: TFilterOption) => d.id === filterId)),
      );

      if (!allSectionsSelectedIds.length) return true;
      if (!selectedValuesForSection.length && sectionCombinationStrategy === 'and') return true;

      switch (type) {
        case 'singleMandatory':
        case 'singleOptional':
        case 'and':
          return selectedValuesForSection.every((v) => predicate(v, entity));
        case 'or':
          return selectedValuesForSection.some((v) => predicate(v, entity));
        default:
          return false;
      }
    });
  }, [filtersInput, sectionCombinationStrategy, selectedFilters]);

  const filteredData = useMemo(
    () =>
      optionsToFilter.filter((item) =>
        sectionCombinationStrategy === 'and'
          ? matchers.every((matcherForSection) => matcherForSection(item))
          : matchers.some((matcherForSection) => matcherForSection(item)),
      ),
    [matchers, optionsToFilter, sectionCombinationStrategy],
  );
  const filteredDataMemoized = useMemoArray(filteredData);

  const trackResultCount = useCallback(() => {
    if (isLoadingData) return;

    // Don't track if there are no filters active, else it tracks on intial load.
    // We have the __FilterReset event to know when the user reset a filter.
    if (!selectedFilters.length) return;

    trackEvent(`${sliceKey}__FilterResult`, {
      resultCount: filteredData.length,
      unfilteredCount: optionsToFilter.length,
    });
  }, [filteredData.length, isLoadingData, sliceKey, selectedFilters.length, optionsToFilter.length]);

  useDebouncedEffect(trackResultCount);

  return useMemo(
    () => ({
      filteredData: filteredDataMemoized,
      resetFilters,
      removeFilter,
      selectedFilters,
      config,
    }),
    [config, filteredDataMemoized, removeFilter, resetFilters, selectedFilters],
  );
};
